import {defineStore} from 'pinia';
import {ref} from 'vue';

const VIEW_OPTS_KEY = 'bi:viewOpts';
const collapsedKey = (dashboardId: string) => `bi:dash:${dashboardId}:collapsed`;

interface ViewOptions {
  showToolbox: boolean;
}

function loadViewOptions(): ViewOptions {
  try {
    const raw = localStorage.getItem(VIEW_OPTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {showToolbox: !!parsed.showToolbox};
    }
  } catch {
  }
  return {showToolbox: false};
}

function persistViewOptions(opts: ViewOptions) {
  try {
    localStorage.setItem(VIEW_OPTS_KEY, JSON.stringify(opts));
  } catch {
  }
}

function loadCollapsed(dashboardId: string): Set<string> {
  try {
    const raw = localStorage.getItem(collapsedKey(dashboardId));
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch {
  }
  return new Set();
}

function persistCollapsed(dashboardId: string, set: Set<string>) {
  try {
    if (set.size === 0)
      localStorage.removeItem(collapsedKey(dashboardId));
    else
      localStorage.setItem(collapsedKey(dashboardId), JSON.stringify(Array.from(set)));
  } catch {
  }
}

export const useDashboardViewOptionsStore = defineStore('dashboardViewOptions', () => {
  const showToolbox = ref<boolean>(loadViewOptions().showToolbox);
  const currentDashboardId = ref<string | undefined>();
  const collapsedWidgets = ref<Set<string>>(new Set());

  function setShowToolbox(value: boolean) {
    showToolbox.value = value;
    persistViewOptions({showToolbox: value});
  }

  function initCollapsedForDashboard(dashboardId: string | undefined) {
    currentDashboardId.value = dashboardId;
    collapsedWidgets.value = dashboardId ? loadCollapsed(dashboardId) : new Set();
  }

  function isCollapsed(widgetId: string | undefined): boolean {
    if (!widgetId) return false;
    return collapsedWidgets.value.has(widgetId);
  }

  function toggleCollapsed(widgetId: string | undefined) {
    if (!widgetId || !currentDashboardId.value) return;
    const next = new Set(collapsedWidgets.value);
    if (next.has(widgetId)) next.delete(widgetId);
    else next.add(widgetId);
    collapsedWidgets.value = next;
    persistCollapsed(currentDashboardId.value, next);
  }

  function forgetWidget(widgetId: string | undefined) {
    if (!widgetId || !currentDashboardId.value) return;
    if (!collapsedWidgets.value.has(widgetId)) return;
    const next = new Set(collapsedWidgets.value);
    next.delete(widgetId);
    collapsedWidgets.value = next;
    persistCollapsed(currentDashboardId.value, next);
  }

  return {
    showToolbox,
    collapsedWidgets,
    setShowToolbox,
    initCollapsedForDashboard,
    isCollapsed,
    toggleCollapsed,
    forgetWidget,
  };
});
